import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData } from 'chart.js';

export interface RevenueTrendPoint {
  month: string;
  value: number;
}

@Component({
  selector: 'app-revenue-trend',
  standalone: true,
  imports: [CommonModule, BaseChartDirective],
  templateUrl: './revenue-trend.component.html',
  styleUrl: './revenue-trend.component.scss',
})
export class RevenueTrendComponent implements OnInit {
  @Input() points: RevenueTrendPoint[] = [];
  @Input() title = 'Revenue Trend';

  lineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#F9FAFB',
        bodyColor: '#F9FAFB',
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context) => `₦${this.formatCompact(context.parsed.y ?? 0)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#6B7280',
          font: { size: 12, family: 'Inter' },
        },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: '#9CA3AF',
          font: { size: 11, family: 'Inter' },
          padding: 8,
          callback: (value) => `₦${this.formatCompact(Number(value))}`,
        },
        grid: {
          color: '#E5E7EB',
          lineWidth: 1,
        },
        border: { display: false },
      },
    },
    elements: {
      point: { radius: 5, hoverRadius: 6, borderWidth: 2 },
      line: { borderWidth: 3, tension: 0.35 },
    },
    animation: { duration: 900, easing: 'easeOutQuart' },
  };

  lineChartData: ChartData<'line'> = {
    labels: [],
    datasets: [
      {
        data: [],
        borderColor: '#0EA5B7',
        backgroundColor: 'rgba(14, 165, 183, 0.12)',
        fill: false,
        pointBackgroundColor: '#0EA5B7',
        pointBorderColor: '#ffffff',
      },
    ],
  };

  ngOnInit(): void {
    this.updateChartData();
  }

  ngOnChanges(): void {
    this.updateChartData();
  }

  private updateChartData(): void {
    if (!this.points?.length) return;

    this.lineChartData = {
      labels: this.points.map((p) => p.month),
      datasets: [
        {
          data: this.points.map((p) => p.value),
          borderColor: '#0EA5B7',
          backgroundColor: 'rgba(14, 165, 183, 0.12)',
          fill: false,
          pointBackgroundColor: '#0EA5B7',
          pointBorderColor: '#ffffff',
        },
      ],
    };
  }

  private formatCompact(value: number): string {
    if (!Number.isFinite(value)) return '0';
    if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return `${Math.round(value)}`;
  }
}
